import { Cart } from './use-cart-actions';
import { useCallback, useState } from 'react';

type CartFlowOrigin = 'all_carts' | 'settings' | null;

interface UseCartFlowOptions {
  reopenSettings: () => void;
}

export const useCartFlow = ({ reopenSettings }: UseCartFlowOptions) => {
  const [isAllCartsOpen, setIsAllCartsOpen] = useState(false);
  const [isCartFormOpen, setIsCartFormOpen] = useState(false);
  const [editingCart, setEditingCart] = useState<Cart | null>(null);
  const [origin, setOrigin] = useState<CartFlowOrigin>(null);

  const start = useCallback((from: CartFlowOrigin) => {
    setOrigin(from);
    if (from === 'all_carts') {
      setIsAllCartsOpen(false);
    }
  }, []);

  const openNewCart = useCallback(
    (from: CartFlowOrigin) => {
      start(from);
      setEditingCart(null);
      setIsCartFormOpen(true);
    },
    [start],
  );

  const openEditCart = useCallback(
    (cart: Cart, from: CartFlowOrigin) => {
      start(from);
      setEditingCart(cart);
      setIsCartFormOpen(true);
    },
    [start],
  );

  const endCartFlow = useCallback(
    (returnToOrigin: boolean) => {
      setIsCartFormOpen(false);
      if (returnToOrigin && origin === 'all_carts') {
        setIsAllCartsOpen(true);
      }
      if (returnToOrigin && origin === 'settings') {
        reopenSettings();
      }
      setOrigin(null);
    },
    [origin, reopenSettings],
  );

  const cancelCartFlow = useCallback(() => endCartFlow(true), [endCartFlow]);
  const completeCartFlow = useCallback(() => endCartFlow(false), [endCartFlow]);

  const openAllCarts = useCallback(() => setIsAllCartsOpen(true), []);
  const closeAllCarts = useCallback(() => setIsAllCartsOpen(false), []);

  return {
    cancelCartFlow,
    closeAllCarts,
    completeCartFlow,
    editingCart,
    isAllCartsOpen,
    isCartFormOpen,
    openAllCarts,
    openEditCart,
    openNewCart,
  };
};
